// Thin, defensive wrapper around the Y8 SDK (minimal-sdk 2.0). EVERY function is a
// no-op unless the SPA is running inside the Y8 portal (see portal.ts), so this file has
// zero effect on itch.io, Game Jolt, CrazyGames or a direct login — the SDK script is
// never even fetched there. Same shape as services/crazygames.ts deliberately: one
// portal, one wrapper. What IS shared is the front door — `services/ads.ts` dispatches to
// whichever portal is active — because the alternative was an `if (isY8) ... else if
// (isCrazyGames) ...` at every ad call site, and four of those drift apart in a month.
//
// Loaded from Y8's CDN and never bundled, which is their explicit requirement ("so SDK
// updates and fixes reach your game without a new build"). Note that loading it from a
// <script> in index.html would have met that requirement too, but would also have made
// every itch.io and CrazyGames player fetch Y8's SDK for nothing — injecting it only on
// Y8 satisfies both rules at once.
//
// Every call is try/catch-guarded: an API mismatch on Y8's side must degrade to "no
// ads", never to a broken game.
//
// Callers do NOT import this file: `services/ads.ts` is the portal-agnostic front door,
// so no page ever branches on which portal it is running in.
import { useSyncExternalStore } from 'react';
import { isY8 } from './portal';
import { authApi } from './api';
import { beginAdBreak, endAdBreak } from './adBreak';

const SDK_SRC = 'https://cdn.y8.com/minimal-sdk/2-0/y8.min.js';

// Assigned by Y8 for ROOTKIN (2026-09). Hard-coded rather than served from /auth/config
// on purpose: their review asks for these two values "exactly as shown", and a constant
// cannot drift from the reviewed build the way an app setting can. They are public
// identifiers — the SDK sends them from the browser — so there is nothing to protect.
const APP_ID  = '6a9ac0655bd49e2858db6d3a';
const GAME_ID = '282193';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySdk = any;

let sdk: AnySdk = null;
let ready = false;
let initPromise: Promise<void> | null = null;
let openingShown = false;  // the type:'start' break is once per page load

// No frequency cap of our own. Y8's docs say "Call the SDK at every natural break anyway"
// and cap it themselves — "one roughly every 180 seconds, configurable per game", rewarded
// never capped — skipping a capped break cheaply and without running its callbacks. An
// earlier 5-minute floor here meant their QA could see fewer ads than they expect. To change
// the pace, ask Y8 to change the per-game interval; do not reintroduce a floor. (CrazyGames
// keeps its own 5-minute floor: different portal, different rules.)

// Ads master switch, driven by server config (Y8:AdsEnabled, default false), so the game
// can ship ad-free on Y8 and have ads flipped on by config alone. Deliberately SEPARATE
// from CrazyGames:AdsEnabled: turning ads on for Y8's review must not turn them on for
// CrazyGames. Reactive, because the +20% buttons appear only once it resolves.
let adsEnabled = false;
const adsListeners = new Set<() => void>();

/** React hook: true only when running on Y8 AND ads are enabled by config. */
export function useY8AdsEnabled(): boolean {
  return useSyncExternalStore(
    (cb) => { adsListeners.add(cb); return () => adsListeners.delete(cb); },
    () => isY8 && adsEnabled,
    () => false,
  );
}

/** True once the SDK has loaded AND init() has returned. Nothing may call an ad before this. */
export function isY8Ready(): boolean { return ready; }

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('y8 sdk load failed'));
    document.head.appendChild(s);
  });
}

/**
 * Load + initialise the Y8 SDK. Memoised so it runs once. No-op off Y8.
 *
 * Y8's own snippet registers the `y8sdk.ready` listener in the page head and then calls
 * `y8.emitReadyEvent()` to cover the case where the async script won the race. We inject
 * the script ourselves AFTER registering the listener, so the race cannot happen — the
 * emitReadyEvent call is kept anyway, guarded, because it is idempotent and it is what
 * their documentation tells reviewers to look for.
 */
export function initY8(): Promise<void> {
  if (!isY8) return Promise.resolve();
  if (!initPromise) initPromise = doInit();
  return initPromise;
}

async function doInit(): Promise<void> {
  // Resolve the ads master switch before anything can request a break. The SDK itself is
  // initialised regardless of it - the switch gates ADS, not the SDK, which Y8's review
  // requires to be present either way.
  try {
    const res = await authApi.getConfig();
    const next = !!(res.data as { y8AdsEnabled?: boolean }).y8AdsEnabled;
    if (adsEnabled !== next) { adsEnabled = next; adsListeners.forEach(l => l()); }
  } catch {
    adsEnabled = false; // no config -> no ads
  }
  return loadAndInit();
}

function loadAndInit(): Promise<void> {
  return new Promise<void>((resolve) => {
    // Resolve either way: a failed SDK must never block the game's own start-up.
    const done = () => resolve();

    const onReady = () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const y8 = (window as any).y8;
        sdk = y8?.sdk?.() ?? null;
        if (!sdk) { done(); return; }

        const appConfig = {
          appId: APP_ID,
          // We do NOT consume a Y8 identity: ROOTKIN has its own username+password
          // accounts and its own server-side progress, so signing a player into Y8
          // silently would buy nothing and confuse whoever is already logged in here.
          // One word to change if achievements or leaderboards are ever wanted.
          autoLogin: false,
        };

        const adConfig = {
          gameId: GAME_ID,
          // Fetch the next ad in the background so a break does not stall on a request.
          preloadAdBreaks: 'on',
          sound: 'on',
          onReady: () => { /* ad system warm; nothing to do until a break is requested */ },
        };

        sdk.init?.(appConfig, adConfig);
        ready = true;
      } catch {
        // SDK unavailable or its API moved — the game runs fine, just without ads.
      }
      done();
    };

    window.addEventListener('y8sdk.ready', onReady, { once: true });

    loadScript(SDK_SRC)
      .then(() => {
        // Belt and braces for a script that loaded before the listener existed.
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const y8 = (window as any).y8;
          if (y8?.emitReadyEvent) y8.emitReadyEvent();
        } catch { /* best-effort */ }
      })
      .catch(() => {
        window.removeEventListener('y8sdk.ready', onReady);
        done();
      });
  });
}


// -- Ads --------------------------------------------------------------------------------
//
// Y8's showAd() is Google's H5 Games Ads adBreak() under another name - same placement
// types, same callbacks, same breakStatus vocabulary - and Y8's own docs confirm Google
// serves the creatives. Two consequences shaped every call below.
//
//  * An interstitial is a FULL-SCREEN overlay the player dismisses. There is no size or
//    position argument anywhere in the API, which answers "can it go in a corner": it
//    cannot. So a break may only be requested at a moment the player has already stopped
//    at to read something.
//  * `adBreakDone` fires on EVERY outcome, including no ad at all, while `afterAd` fires
//    only when an ad was actually shown. So each promise settles on `adBreakDone`, and
//    every settle is idempotent - a callback arriving after the timeout cannot resolve twice.
//
// PAUSE AND MUTE, which Y8's QA checks explicitly ("Gameplay pauses and audio mutes when ads
// appear"): `beforeAd` begins the break and `afterAd` ends it — see services/adBreak.ts for
// what that pauses. An earlier version of this file argued there was nothing to mute. That
// was wrong: the transfer ticker plays a cue when a download finishes, which can land in the
// middle of an ad. Two details:
//  * the break is begun ONLY in `beforeAd`, which fires only when an ad actually shows — "Only
//    pause if ad actually displays" — so a capped or empty break never pauses anything;
//  * it is ended on `afterAd` AND on `adBreakDone`, and adBreak.ts has its own failsafe, so
//    no missing callback can leave the game silent. The promise's own timeout only releases
//    the caller; it does not un-pause, because an ad may still genuinely be on screen.

/** Request a break at a natural stopping point; resolves when it is over. Y8 cap the rate. */
export function y8Interstitial(type: 'next' | 'start', name: string): Promise<void> {
  if (!isY8 || !adsEnabled || !ready || !sdk?.showAd) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let settled = false;
    const release = () => { if (!settled) { settled = true; resolve(); } };
    const done = () => { endAdBreak(); release(); };
    try {
      sdk.showAd({ type, name, beforeAd: beginAdBreak, afterAd: done, adBreakDone: done });
    } catch {
      done();
      return;
    }
    setTimeout(release, 30_000); // safety net: never hang the caller on a stuck ad
  });
}

/**
 * The opening break (type 'start'), at most once per page load.
 *
 * The caller decides WHO sees it - a player who has finished or skipped the tutorial,
 * never a brand-new one - because a full-screen ad served to someone who has not played a
 * minute is the fastest way to lose them, and ROOTKIN's first impression is already a
 * text-dense console. Keeping that rule at the call site rather than here puts the
 * condition next to the state it reads.
 */
export function y8OpeningAd(name: string): Promise<void> {
  if (!isY8 || openingShown) return Promise.resolve();
  openingShown = true;
  return y8Interstitial('start', name);
}

/**
 * A REWARDED break (player opted in). Resolves true ONLY when the ad was actually
 * watched, so every other outcome - dismissed early, none preloaded, not ready, timed
 * out, an error - pays the base amount and never shows the player a failure.
 *
 * Y8 hand back a `showAdFn` in `beforeReward` which must be called to play the ad; not
 * calling it is how you decline. Rewarded breaks are never frequency-capped. They pause and
 * mute exactly like an interstitial: `beforeAd`/`afterAd` apply to every placement.
 */
export function y8Rewarded(name: string): Promise<boolean> {
  if (!isY8 || !adsEnabled || !ready || !sdk?.showAd) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => { if (!settled) { settled = true; resolve(ok); } };
    try {
      sdk.showAd({
        type: 'reward',
        name,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        beforeReward: (showAdFn: any) => { try { showAdFn(); } catch { endAdBreak(); finish(false); } },
        beforeAd:    beginAdBreak,
        afterAd:     endAdBreak,
        adViewed:    () => finish(true),
        adDismissed: () => finish(false),
        // Fires on every outcome. Used only to settle a NEGATIVE: when the ad was viewed
        // adViewed has already won the race, and finish is idempotent. It also ends the
        // break, in case afterAd never came.
        adBreakDone: (info: { breakStatus?: string } | undefined) => {
          endAdBreak();
          if (info?.breakStatus !== 'viewed') finish(false);
        },
      });
    } catch {
      endAdBreak();
      finish(false);
      return;
    }
    setTimeout(() => finish(false), 60_000); // safety net
  });
}
